import { WorkerInviteStatus } from '../../../__generated__/graphql';
import type { BadgeTone } from '../../components/ui';

/**
 * Worker logins (spec 019 D14b): the Provider invites by email or phone and
 * sends the link themselves (there is no SMS sender), the worker signs in and
 * accepts. Pure helpers for the accounts page and the invite landing page.
 */

export type InviteTarget = { email: string } | { phone: string };

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

/**
 * An email address or a phone number from what the Provider typed. US numbers
 * without a country code get +1; others need the leading +.
 */
export function inviteTargetFrom(text: string): { target?: InviteTarget; error?: string } {
  const value = text.trim();
  if (!value) return { error: 'Enter an email address or a phone number' };
  if (value.includes('@')) {
    return EMAIL.test(value)
      ? { target: { email: value.toLowerCase() } }
      : { error: 'This email address does not look right' };
  }
  const digits = value.replace(/[^\d]/g, '');
  if (value.startsWith('+')) {
    return digits.length >= 8 && digits.length <= 15
      ? { target: { phone: `+${digits}` } }
      : { error: 'This phone number does not look right' };
  }
  if (digits.length === 10) return { target: { phone: `+1${digits}` } };
  if (digits.length === 11 && digits.startsWith('1')) return { target: { phone: `+${digits}` } };
  return { error: 'Enter a 10-digit US number, or start with + and the country code' };
}

export interface InviteAccount {
  status: WorkerInviteStatus;
  inviteExpiresAt?: string | null;
}

/** The status line of a login, with an expired invite told apart. */
export function inviteStatusView(
  account: InviteAccount,
  now: number = Date.now(),
): { label: string; tone: BadgeTone; expired: boolean } {
  if (account.status === WorkerInviteStatus.Active) {
    return { label: 'Active', tone: 'success', expired: false };
  }
  if (account.status === WorkerInviteStatus.Removed) {
    return { label: 'Removed', tone: 'neutral', expired: false };
  }
  const expires = account.inviteExpiresAt ? Date.parse(account.inviteExpiresAt) : NaN;
  if (Number.isFinite(expires) && expires <= now) {
    return { label: 'Invite expired', tone: 'error', expired: true };
  }
  return { label: 'Invited', tone: 'warning', expired: false };
}

/** The token of `/accept-worker-invite?token=…`, or null. */
export function inviteTokenFrom(search: string): string | null {
  const token = new URLSearchParams(search).get('token')?.trim();
  return token ? token : null;
}

/** A message the Provider can paste into a text or an email. */
export function inviteMessage(inviteUrl: string, workerName: string, brandName: string): string {
  return `You're invited to sign in as ${workerName} on ${brandName}. Open this link to accept: ${inviteUrl}`;
}
